import { forwardRef, ReactNode } from "react";
import { css, cx } from "styled-system/css";

import { Button, ButtonProps } from "./button";

export interface FancyButtonProps extends ButtonProps {
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

const mrs = {
  0: 10,
  1: 4,
  2: -4,
};

const Sides = ({ side, disabled = false }: { side: "left" | "right"; disabled?: boolean }) => {
  return (
    <div
      className={css({
        display: "flex",
        z: "1",
        position: "relative",
        overflow: "visible",
        height: "[38px]",
        minWidth: "14",
        "&[data-disabled=false]": {
          _groupHover: {
            bg: "brandYellow.400",
          },
        },
        "&[data-disabled=true]": {
          bg: "brandYellow.400",
        },
      })}
      style={{
        transform: `rotate(${side === "left" ? "0deg" : "180deg"})`,
        width: "14px", // To cover for the cirlces inline to the button
      }}
    >
      {[0, 1, 2].map((i) => {
        const clipPath = `circle(50% at ${110 + (3 - i) * 10}% 50%)`;

        return (
          <div
            key={i}
            data-disabled={disabled}
            className={css({
              w: "[38px]",
              h: "[38px]",
              position: "absolute",
              transitionPreset: "colors",
              bg: "brandYellow.500",
              _groupHover: {
                "&:not([data-disabled=true])": {
                  bg: "brandYellow.400",
                },
              },
              _groupActive: {
                "&:not([data-disabled=true])": {
                  bg: "brandYellow.400",
                },
              },
            })}
            style={{
              clipPath,
              right: `${mrs[i as keyof typeof mrs]}px`,
            }}
          />
        );
      })}
    </div>
  );
};

export const FancyButton = forwardRef<HTMLButtonElement, FancyButtonProps>(
  ({ children, className, fullWidth, ...props }, ref) => {
    const disabled = props.disabled || props.loading;
    return (
      <div
        className={cx(containerStyle, className) + " group"}
        data-disabled={disabled}
        data-full-width={fullWidth}
      >
        <Sides side="left" disabled={disabled} />
        <Button variant="text" ref={ref} className={fancyButtonStyle} {...props}>
          <span className="label">{children}</span>
        </Button>
        <Sides side="right" disabled={disabled} />
      </div>
    );
  },
);
FancyButton.displayName = "FancyButton";

const containerStyle = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  "&[data-disabled=true]": {
    opacity: 0.3,
  },
  "&[data-full-width=true]": {
    w: "full",
  },
});

const fancyButtonStyle = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "[3px]",
  bg: "brandYellow.500",
  color: "black",
  fontWeight: "regular",
  rounded: "full",
  w: "full",
  px: "20",
  h: "48",
  z: "1",
  textTransform: "uppercase",
  fontSize: "12",
  position: "relative",
  overflow: "hidden",
  border: "none",
  letterSpacing: "1.5px",
  "&:not(:disabled)": {
    _groupHover: {
      bg: "brandYellow.400",
    },
    _groupActive: {
      bg: "brandYellow.400",
    },
  },
  "&:hover:not(:disabled)": {
    color: "black",
    bg: "brandYellow.400",
  },
  "&:active:not(:disabled)": {
    color: "black",
    bg: "brandYellow.400",
  },
  "&:disabled:not([data-loading=true])": {
    color: "black",
    bg: "brandYellow.500",
  },
});
